import { Component, OnChanges, SimpleChanges, inject, ChangeDetectionStrategy, signal, input, output, linkedSignal } from '@angular/core';
import {RatingsModel} from '../../model/ratings-model';
import {SelectionModelSizeCharacteristic} from '../../model/selection-model-size-characteristic';
import {GuruAllowable} from '../../model/guru-allowable';
import {SelectionItem} from '../../selections/selection-item';
import {MotorType} from '../../model/motor-type';
import {UnitRatingRequest} from '../../model/unit-rating-request';
import {Entity} from '../../entity/entity';
import {TagInformation} from '../../model/tag-information';
import {EntityLine} from '../../entity/entity-line';
import {RatingUnit} from '../../model/rating-unit';
import {ScheduleService} from '../../services/schedule.service';
import {UnitSelectionService} from '../../services/unit-selection-service';
import {RatingService} from '../../services/rating.service';
import {ProductService} from '../../services/product-service';
import {ModelService} from '../../services/model-service';
import {Observable, Subject} from 'rxjs';
import {StringUtils} from '../../utils/string-utils';
import {ScheduleLineInput} from '../../model/schedule-line-input';
import {TagUtils} from '../../utils/tag-utils';
import {CoilRequest} from '../../model/coil-request';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {DialogService} from '../../services/dialog.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ScheduleLinePerformanceOutputComponent} from '../../schedules/schedule-line-performance-output/schedule-line-performance-output.component';
import {ScheduleLinePerformanceInputComponent} from '../../schedules/schedule-line-performance-input/schedule-line-performance-input.component';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';

import {BrandID} from "../../enums/brand-id";

@Component({
    selector: 'app-ratings-tool-form',
    templateUrl: './ratings-tool-form.component.html',
    styleUrls: ['./ratings-tool-form.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, ScheduleLinePerformanceInputComponent, ScheduleLinePerformanceOutputComponent, MatProgressSpinner]
})
export class RatingsToolFormComponent implements OnChanges {
  private readonly scheduleService = inject(ScheduleService);
  private readonly unitSelectionService = inject(UnitSelectionService);
  private readonly ratingService = inject(RatingService);
  private readonly productService = inject(ProductService);
  private readonly modelService = inject(ModelService);
  private readonly dialogService = inject(DialogService);


  readonly unitToUpdateInput = input.required<RatingUnit>({ alias: 'unitToUpdate' });
  readonly workingUnit = linkedSignal(() => this.unitToUpdateInput());
  readonly schedule = input<Entity>(undefined!);
  readonly scheduleLine = input<EntityLine>(undefined!);
  readonly unitToUpdateChange = output<RatingUnit>();
  readonly tagsUpdated = output<TagInformation[]>();
  readonly productIds = signal<string[]>(undefined!);
  readonly allModels = signal<RatingsModel[]>(undefined!);
  readonly availableModels = signal<RatingsModel[]>(undefined!);
  readonly availableSizes = signal<SelectionModelSizeCharacteristic[]>(undefined!);
  readonly systemTypes = signal<GuruAllowable[]>(undefined!);
  readonly coilRows = signal<SelectionItem[]>(undefined!);
  readonly availableMotorTypes = signal<MotorType[]>(undefined!);
  readonly availableMotorVoltages = signal<SelectionItem[]>(undefined!);
  readonly showCoolingInputs = signal(false);
  readonly showHeatingInputs = signal(false);
  readonly showHydronicHeating = signal(false);
  readonly showElectricHeating = signal(false);
  readonly engineKey = signal<string>(undefined!);
  readonly ratingsError = signal<string>(undefined!);
  readonly isRefrigerant = signal<boolean>(undefined!);
  readonly isSteam = signal<boolean>(undefined!);
  readonly availableElectricHeatKw = signal<SelectionItem[]>(undefined!);
  readonly availableFilters = signal<SelectionItem[]>(undefined!);
  readonly availablePrimaryCoilCircuits = signal<SelectionItem[]>(undefined!);
  readonly availableSecondaryCoilCircuits = signal<SelectionItem[]>(undefined!);
  readonly isAirHandlerModel = signal<boolean>(false);
  readonly isSureflowModel = signal<boolean>(false);
  readonly tags = signal<TagInformation[]>(undefined!);
  readonly quantity = signal<number>(undefined!);
  readonly initialLoadComplete = signal<boolean>(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unitToUpdateInput']) {
      if (!changes['unitToUpdateInput'].firstChange) {
        if (this.workingUnit() && !this.workingUnit().request) {
          this.workingUnit().request = this.initialRatingRequest();
        }
        this.isBusy.set(true);
        this.availableModels.set([]);
        this.loadLineDetail();
      } else {
        this.initializeInputs();
      }
    } else {
      this.initializeInputs();
    }
  }

  private initializeInputs(): void {
    this.isBusy.set(true);
    this.unitSelectionService.getRatingsModels().subscribe({
      next: (models) => {
        this.allModels.set(models);
        this.productService.getProductConfigurations(BrandID.CLM).subscribe({
          next: (products) => {
            this.productIds.set(products.filter(product => models.flatMap(model => model.productId).indexOf(product.productId) >= 0)
              .sort((first, second) => first.productId.localeCompare(second.productId))
              .map(product => product.productId));
            if (this.schedule()) {
              this.quantity.set(1);
              const tagInformation = new TagInformation();
              tagInformation.sequence = 1;
              this.tags.set([tagInformation]);
              this.tagsUpdated.emit(this.tags());
            }
            if (this.scheduleLine()) {
              this.quantity.set(this.scheduleLine().quantityExtended);
              this.tags.set(TagUtils.createTaggingDetails(this.scheduleLine().singleEntityLines));
              this.tagsUpdated.emit(this.tags());
              this.scheduleService.getScheduleLineDetail(this.scheduleLine().entityKey.entityId, this.scheduleLine().lineId).subscribe({
                next: (detail) => {
                  this.workingUnit.set(this.convertScheduleLineInput(detail));
                  this.loadLineDetail();
                }, error: (error) => this.handleError(error)
              });
            } else {
              this.loadLineDetail();
            }
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private convertScheduleLineInput(input: ScheduleLineInput): RatingUnit {
    const unit: RatingUnit = new RatingUnit();
    unit.orgUnitId = this.schedule().repOfficeId;
    unit.request = input.candidate.ratingRequest;
    unit.motorTypeCode = input.candidate.motorType.guruModelValue;
    unit.coilRowCode = input.candidate.coilRowCode;
    unit.productId = input.line.product;
    unit.modelCode = input.candidate.ratingRequest.Model;
    unit.sizeCode = input.candidate.ratingRequest.Size;
    unit.response = input.candidate.ratingResponse;
    unit.systemTypeCode = input.candidate.systemType.code;
    unit.electricHeatCode = input.candidate.ratingRequest.ElectricHeatRequest?.ElectricHeatKilowatts?.toString() || 'None';
    unit.powerSupplyCode = input.candidate.ratingRequest.MotorConfiguration;
    return unit;
  }

  private loadLineDetail(): void {
    if (this.workingUnit()) {
      if (!StringUtils.isEmptyNumericValue(+this.workingUnit().electricHeatCode) && +this.workingUnit().electricHeatCode < 1000) {
        this.workingUnit().electricHeatCode = (+this.workingUnit().electricHeatCode * 1000).toString();
      }
      this.workingUnit().primaryCoilCircuits = (this.workingUnit().request?.PrimaryCoilRequest?.NumberOfCircuits || 0).toString();
      this.workingUnit().secondaryCoilCircuits = (this.workingUnit().request?.SecondaryCoilRequest?.NumberOfCircuits || 0).toString();
      this.handleProductChange().subscribe({
        complete: () => {
          this.handleModelChange().subscribe({
            complete: () => {
              this.initialLoadComplete.set(true);
              this.updateUnitRating().subscribe({
                complete: () => this.isBusy.set(false),
                error: (error) => this.handleError(error)
              });
            }, error: (error) => this.handleError(error)
          });
        }, error: (error) => this.handleError(error)
      });
    } else {
      this.workingUnit.set(new RatingUnit());
      this.workingUnit().request = this.initialRatingRequest();
      this.initialLoadComplete.set(true);
      this.isBusy.set(false);
    }
  }

  getModelImageFileName(): string {
    if ((this.allModels()?.length || 0) > 0) {
      const selectedModelCode = (this.workingUnit().modelCode || '');
      const filtered = this.allModels().filter(model => model.modelCode === selectedModelCode);
      if (filtered.length === 1) {
        return filtered[0].imageFileName;
      }
    }
    return '';
  }

  selectedMotorVoltageChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.availableMotorTypes.set([]);
    this.handleSelectedMotorVoltageChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          error: (error) => this.handleError(error),
          complete: () => this.isBusy.set(false)});
      }, error: (error) => this.handleError(error)
    });
  }

  private handleSelectedMotorVoltageChange(): Observable<void> {
    const subject = new Subject<void>();
    this.workingUnit().powerSupplyCode = (this.workingUnit()?.powerSupplyCode?.replace(/\//g, '') || undefined)!;
    if ((this.availableMotorVoltages()?.length || 0) > 0) {
      const filtered = this.availableMotorVoltages().filter(voltage => voltage.code === this.workingUnit().powerSupplyCode);
      if (filtered.length !== 1) {
        this.workingUnit().powerSupplyCode = undefined!;
        if (this.workingUnit().request?.ElectricHeatRequest) {
          this.workingUnit().request.ElectricHeatRequest.ElectricHeatVoltage = undefined!;
        }
        subject.complete();
      } else {
        this.setElectricHeatVoltage();
        this.modelService.setValue('USUnitVoltage', this.engineKey(), this.workingUnit().powerSupplyCode).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.modelService.getCharacteristicValue(this.engineKey(), 'USUnitVoltage', 'Description').subscribe({
                  next: (code) => {
                    this.workingUnit().request.MotorConfiguration = code;
                    if (this.workingUnit().request?.ElectricHeatRequest) {
                      this.workingUnit().request.ElectricHeatRequest.ElectricHeatVoltage = code;
                    }
                    this.unitSelectionService.getEngineMotorTypes(this.engineKey()).subscribe({
                      next: (motorTypes: MotorType[]) => {
                        this.availableMotorTypes.set(motorTypes);
                        this.handleSelectedMotorTypeChange().subscribe({
                          complete: () => {
                            subject.complete();
                          }, error: (error) => subject.error(error)
                        });
                      }, error: (error) => subject.error(error)
                    });
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  showTagsDialog(): void {
    if (this.quantity() > 0) {
      if (this.tags().length > this.quantity()) {
        this.tags.set(this.tags().slice(0, this.quantity()));
      }
      this.dialogService.showTagManagementDialog(this.tags(), 460, this.quantity(), false).subscribe({
        next: (results: TagInformation[]) => {
          if (results && results.length > 0) {
            this.tags.set(results);
            this.quantity.set(this.tags().length);
            this.tagsUpdated.emit(this.tags());
          }
        }
      });
    }
  }

  quantityUpdated(): void {
    if (this.tags().length > this.quantity()) {
      this.tags.set(this.tags().slice(0, this.quantity()));
    } else if (this.tags().length < this.quantity()) {
      for (let index = this.tags().length; index < this.quantity(); index++) {
        const tag: TagInformation = new TagInformation();
        tag.sequence = index + 1;
        this.tags.update(current => [...current, tag]);
      }
    }
    this.tagsUpdated.emit(this.tags());
  }

  private updateUnitRating(): Observable<void> {
    this.workingUnit().response = undefined!;
    this.unitToUpdateChange.emit(this.workingUnit());
    const subject: Subject<void> = new Subject<void>();
    if (this.initialLoadComplete()) {
      if (this.isValidRequest()) {
        if (this.workingUnit().request.PrimaryCoilRequest) {
          this.workingUnit().request.PrimaryCoilRequest.NumberOfCircuits = +this.workingUnit().primaryCoilCircuits;
        }
        if (this.workingUnit().request.SecondaryCoilRequest) {
          this.workingUnit().request.SecondaryCoilRequest.NumberOfCircuits = +this.workingUnit().secondaryCoilCircuits;
        }
        this.ratingService.getUnitRatingResponse(this.workingUnit().request).subscribe({
          next: (response) => {
            this.workingUnit().response = response;
            if (response.ErrorMessage) {
              this.ratingsError.set(response.ErrorMessage);
            }
            if (StringUtils.isBlank(this.workingUnit().primaryCoilCircuits) || StringUtils.equalsIgnoreCase('0', this.workingUnit().primaryCoilCircuits)) {
              this.workingUnit().primaryCoilCircuits = (response?.PrimaryCoilResponse?.NumberOfCircuits || 0).toString();
            }
            this.unitToUpdateChange.emit(this.workingUnit());
            subject.complete();
          },
          error: (error) => subject.error(error),
          complete: () => subject.complete()
        });
      } else {
        subject.complete();
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private isValidRequest(): boolean {
    if (!this.workingUnit().request) {
      return false;
    }
    if (!this.workingUnit().modelCode) {
      return false;
    }
    if (!this.workingUnit().sizeCode) {
      return false;
    }
    if (!this.workingUnit().systemTypeCode) {
      return false;
    }
    if (!this.workingUnit().coilRowCode) {
      return false;
    }
    if (!this.workingUnit().powerSupplyCode) {
      return false;
    }
    if (!this.workingUnit().motorTypeCode) {
      return false;
    }
    if ((this.availableFilters()?.length || 0) > 0 && !this.workingUnit().request.Filter) {
      return false;
    }
    if (this.showElectricHeating() && !this.workingUnit().electricHeatCode) {
      return false;
    }
    if (this.showCoolingInputs()) {
      if (this.isZero(this.workingUnit().request.PrimaryCoilRequest?.EnteringDryBulb)) {
        return false;
      }
      if (this.isZero(this.workingUnit().request.PrimaryCoilRequest?.EnteringWetBulb)) {
        return false;
      }
      if (this.isRefrigerant()) {
        if (this.isZero(this.workingUnit().request.PrimaryCoilRequest?.SaturationSuctionTemperature)) {
          return false;
        }
      } else {
        if (this.isZero(this.workingUnit().request.PrimaryCoilRequest?.EnteringFluidTemperature)) {
          return false;
        }
      }
      if (this.showHeatingInputs()) {
        if (this.showElectricHeating()) {
          if (this.isZero(this.workingUnit().request.ElectricHeatRequest?.EnteringDryBulb)) {
            return false;
          }
        } else {
          if (this.isZero(this.workingUnit().request.SecondaryCoilRequest?.EnteringDryBulb)) {
            return false;
          }
          if (this.isSteam()) {
            if (this.isZero(this.workingUnit().request.SecondaryCoilRequest?.EnteringSteamPressure)) {
              return false;
            }
          } else if (this.showHydronicHeating()) {
            if (this.isZero(this.workingUnit().request.SecondaryCoilRequest?.EnteringFluidTemperature)) {
              return false;
            }
          }
        }
      }
    } else if (this.showHeatingInputs()) {
      if (this.showElectricHeating()) {
        if (this.isZero(this.workingUnit().request.ElectricHeatRequest?.EnteringDryBulb)) {
          return false;
        }
      } else {
        if (this.isZero(this.workingUnit().request.PrimaryCoilRequest?.EnteringDryBulb)) {
          return false;
        }
        if (this.isSteam()) {
          if (this.isZero(this.workingUnit().request.PrimaryCoilRequest?.EnteringSteamPressure)) {
            return false;
          }
        } else if (this.showHydronicHeating()) {
          if (this.isZero(this.workingUnit().request.PrimaryCoilRequest?.EnteringFluidTemperature)) {
            return false;
          }
        }
      }
    }
    return true;
  }

  private isZero(input: number): boolean {
    return isNaN(input) || (input || 0) === 0;
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.initialLoadComplete.set(true);
    if (error.error) {
      this.errorMessage.set(error.error);
    } else {
      this.errorMessage.set('An error has occurred.  Please contact Customer Service');
    }
  }

  productIdChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.workingUnit().systemTypeCode = undefined!;
    this.initialLoadComplete.set(false);
    this.handleProductChange().subscribe({
      complete: () => {
        this.handleModelChange().subscribe({
          complete: () => {
            this.isBusy.set(false);
            this.initialLoadComplete.set(true);
          },
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleProductChange(): Observable<void> {
    this.availableModels.set([]);
    this.workingUnit().response = undefined!;
    const subject: Subject<void> = new Subject<void>();
    if (this.workingUnit().productId && this.workingUnit().productId.length > 0) {
      this.availableModels.set(this.allModels().filter(model => model.productId === this.workingUnit().productId));
      if (this.workingUnit().modelCode) {
        const filteredModels = this.availableModels().filter(model => model.modelCode === this.workingUnit().modelCode);
        if (filteredModels.length !== 1) {
          if (this.workingUnit().request) {
            this.workingUnit().request.Model = undefined!;
          }
          this.workingUnit().modelCode = undefined!;
        }
      }
      this.modelService.getEngineKey(this.workingUnit().productId).subscribe({
        next: (key) => {
          this.engineKey.set(key);
          this.loadSystemTypes().subscribe({
            next: () => subject.next(),
            complete: () => subject.complete(),
            error: (error) => subject.error(error),
          });
        },
        error: (error) => subject.error(error)
      });
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private loadSystemTypes(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    this.coilRows.set([]);
    if (this.engineKey()) {
      this.modelService.isComplete(this.engineKey()).subscribe({
        next: () => {
          this.modelService.getEntity(this.engineKey(), 'USSystemType').subscribe({
            next: (entity) => {
              this.systemTypes.set(JSON.parse(JSON.stringify(entity.Allowables)));
              this.handleSystemTypeChange().subscribe({
                next: () => subject.next(),
                error: (error) => subject.error(error),
                complete: () => subject.complete()
              });
            },
            error: (error) => subject.error(error),
          });
        },
        error: (error) => subject.error(error)
      });
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  systemTypeChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.workingUnit().request.PrimaryCoilRequest = undefined!;
    this.workingUnit().request.SecondaryCoilRequest = undefined!;
    this.workingUnit().request.ElectricHeatRequest = undefined!;
    this.handleSystemTypeChange().subscribe({
      complete: () => this.isBusy.set(false),
      error: (error) => this.handleError(error)
    });
  }

  private handleSystemTypeChange(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    this.showCoolingInputs.set(false);
    this.showHydronicHeating.set(false);
    this.showHeatingInputs.set(false);
    if (this.workingUnit().systemTypeCode?.length || 0 > 0) {
      const filtered = this.systemTypes().filter(systemType => systemType.Value === this.workingUnit().systemTypeCode);
      if (filtered.length === 1) {
        this.modelService.setValue('USSystemType', this.engineKey(), filtered[0].Value).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.modelService.getCharacteristicValue(this.engineKey(), 'USSystemType', 'PriCoilApp').subscribe({
                  next: (primaryApplication) => {
                    this.modelService.getCharacteristicValue(this.engineKey(), 'USSystemType', 'SecCoilApp').subscribe({
                      next: (secondaryApplication) => {
                        this.modelService.getCharacteristicValue(this.engineKey(), 'USSystemType', 'SteamCoil').subscribe({
                          next: (isSteam) => {
                            this.isSteam.set(StringUtils.equalsIgnoreCase(isSteam, 'True'));
                            this.modelService.getCharacteristicValue(this.engineKey(), 'USSystemType', 'DxCoil').subscribe({
                              next: (isRefrigerant) => {
                                this.isRefrigerant.set(StringUtils.equalsIgnoreCase(isRefrigerant, 'True'));
                                this.modelService.getCharacteristicValue(this.engineKey(), 'USSystemType', 'ElecHt').subscribe({
                                  next: (hasElectricHeat) => {
                                    this.showCoolingInputs.set(StringUtils.equalsIgnoreCase(primaryApplication, 'C'));
                                    this.showHydronicHeating.set(StringUtils.equalsIgnoreCase(primaryApplication, 'H') || StringUtils.equalsIgnoreCase(secondaryApplication, 'H'));
                                    this.showElectricHeating.set(StringUtils.equalsIgnoreCase(hasElectricHeat, 'True'));
                                    this.showHeatingInputs.set(this.showHydronicHeating() || this.showElectricHeating());
                                    if (this.showCoolingInputs()) {
                                      this.workingUnit().request.PrimaryCoilRequest = this.buildDefaultCoolingRequest();
                                      if (this.showHydronicHeating()) {
                                        this.workingUnit().request.SecondaryCoilRequest = this.buildDefaultHeatingRequest();
                                      } else {
                                        this.workingUnit().request.SecondaryCoilRequest = undefined!;
                                      }
                                    } else if (this.showHydronicHeating()) {
                                      this.workingUnit().request.PrimaryCoilRequest = this.buildDefaultHeatingRequest();
                                      this.workingUnit().request.SecondaryCoilRequest = undefined!;
                                    }
                                    if (this.showElectricHeating()) {
                                      this.workingUnit().request.ElectricHeatRequest = this.buildDefaultElectricHeatRequest();
                                      this.setElectricHeatVoltage();
                                    }
                                    this.loadCoilRows().subscribe({
                                      complete: () => {
                                        if (this.coilRows().length === 0) {
                                          this.workingUnit().coilRowCode = undefined!;
                                          if (this.workingUnit().request.PrimaryCoilRequest) {
                                            this.workingUnit().request.PrimaryCoilRequest.RowCount = 0;
                                            this.workingUnit().request.PrimaryCoilRequest.NumberOfCircuits = 0;
                                          }
                                          if (this.workingUnit().request.SecondaryCoilRequest) {
                                            this.workingUnit().request.SecondaryCoilRequest.RowCount = 0;
                                            this.workingUnit().request.SecondaryCoilRequest.NumberOfCircuits = 0;
                                          }
                                          this.workingUnit().response = undefined!;
                                        }
                                        this.handleCoilRowsChange().subscribe({
                                          complete: () => {
                                            this.updateUnitRating().subscribe({
                                              next: () => subject.complete(),
                                              error: (error) => subject.error(error),
                                              complete: () => subject.complete()
                                            });
                                          }, error: (error) => subject.error(error)
                                        });
                                      }, error: (error) => subject.error(error)
                                    });
                                  }, error: (error) => subject.error(error)
                                });
                              }, error: (error) => subject.error(error)
                            });
                          }, error: (error) => subject.error(error)
                        });
                      }, error: (error) => subject.error(error)
                    });
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      } else {
        this.workingUnit().systemTypeCode = undefined!;
        this.workingUnit().request = this.initialRatingRequest();
        subject.complete();
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private setElectricHeatVoltage(): void {
    if (this.workingUnit().powerSupplyCode && this.availableMotorVoltages()?.length > 0) {
      if (this.workingUnit().request?.ElectricHeatRequest) {
        const filteredVoltage = this.availableMotorVoltages().filter(voltage => this.workingUnit().powerSupplyCode === voltage.code);
        if (filteredVoltage.length === 1) {
          this.workingUnit().request.ElectricHeatRequest.ElectricHeatVoltage = filteredVoltage[0].description;
        }
      }
    }
  }

  private loadCoilRows(): Observable<void> {
    this.coilRows.set([]);
    const subject = new Subject<void>();
    this.modelService.getEntity(this.engineKey(), 'USCoilRows').subscribe({
      next: (coilRowEntity) => {
        for (const allowable of coilRowEntity.Allowables) {
          if (allowable.IsValid) {
            const coilRow: SelectionItem = new SelectionItem();
            coilRow.code = allowable.Value;
            coilRow.description = allowable.Description;
            this.coilRows.update(current => [...current, coilRow]);
          }
        }
        subject.complete();
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private loadCoilCircuits(): Observable<void> {
    const subject = new Subject<void>();
    this.loadPrimaryCoilCircuits().subscribe({
      complete: () => {
        this.loadSecondaryCoilCircuits().subscribe({
          complete: () => {
            subject.next();
            subject.complete();
          }, error: (error) => subject.error(error)
        });
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private loadPrimaryCoilCircuits(): Observable<void> {
    const subject = new Subject<void>();
    this.availablePrimaryCoilCircuits.set([]);
    if (this.engineKey()) {
      this.unitSelectionService.getPrimaryCoilCircuits(this.engineKey()).subscribe({
        next: (circuits: SelectionItem[]) => {
          this.availablePrimaryCoilCircuits.set(circuits);
          subject.next();
          subject.complete();
        }, error: (error) => subject.error(error)
      });
    } else {
      setTimeout(() => {
        subject.next();
        subject.complete();
      }, 10);
    }
    return subject.asObservable();
  }

  private loadSecondaryCoilCircuits(): Observable<void> {
    const subject = new Subject<void>();
    this.availableSecondaryCoilCircuits.set([]);
    if (this.engineKey()) {
      this.unitSelectionService.getSecondaryCoilCircuits(this.engineKey()).subscribe({
        next: (circuits: SelectionItem[]) => {
          this.availableSecondaryCoilCircuits.set(circuits);
          subject.next();
          subject.complete();
        }, error: (error) => subject.error(error)
      });
    } else {
      setTimeout(() => {
        subject.next();
        subject.complete();
      }, 10);
    }
    return subject.asObservable();
  }

  private loadElectricHeatKw(): Observable<void> {
    const subject = new Subject<void>();
    this.availableElectricHeatKw.set([]);
    if (this.showElectricHeating()) {
      this.modelService.getEntity(this.engineKey(), 'USElecHtKw').subscribe({
        next: (electricHeat) => {
          for (const allowable of electricHeat.Allowables) {
            if (allowable.IsValid) {
              const heatKw = new SelectionItem();
              heatKw.code = allowable.Value;
              heatKw.description = allowable.Description;
              this.availableElectricHeatKw.update(current => [...current, heatKw]);
            }
          }
          subject.complete();
        }, error: (error) => subject.error(error)
      });
    } else {
      this.workingUnit().request.ElectricHeatRequest = undefined!;
      this.workingUnit().electricHeatCode = 'None';
      subject.complete();
    }
    return subject.asObservable();
  }

  electricHeatKwChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleElectricHeatKwChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleElectricHeatKwChange(): Observable<void> {
    const subject = new Subject<void>();
    if (this.workingUnit().electricHeatCode && (this.availableElectricHeatKw()?.length || 0) > 0) {
      const filtered = this.availableElectricHeatKw().filter(row => row.code === this.workingUnit().electricHeatCode);
      if (filtered.length !== 1) {
        this.workingUnit().electricHeatCode = undefined!;
        subject.complete();
      } else {
        if (StringUtils.isEmptyNumericValue(+this.workingUnit().electricHeatCode)) {
          this.workingUnit().request.ElectricHeatRequest = undefined!;
        } else {
          if (!this.workingUnit().request.ElectricHeatRequest) {
            this.workingUnit().request.ElectricHeatRequest = this.buildDefaultElectricHeatRequest();
          }
          this.setElectricHeatVoltage();
          this.workingUnit().request.ElectricHeatRequest.ElectricHeatKilowatts = (+this.workingUnit().electricHeatCode) / 1000;
        }
        subject.complete();
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  coilRowsChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleCoilRowsChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  primaryCircuitsChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handlePrimaryCoilCircuitsChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  secondaryCircuitsChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleSecondaryCoilCircuitsChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleCoilCircuitsChange(): Observable<void> {
    const subject = new Subject<void>();
    this.handlePrimaryCoilCircuitsChange().subscribe({
      complete: () => {
        this.handleSecondaryCoilCircuitsChange().subscribe({
          complete: () => {
            subject.next();
            subject.complete();
          },
          error: (error) => subject.error(error)
        });
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private handlePrimaryCoilCircuitsChange(): Observable<void> {
    const subject = new Subject<void>();
    if ((this.availablePrimaryCoilCircuits()?.length || 0) > 0) {
      const filtered = this.availablePrimaryCoilCircuits().filter(row => row.code === this.workingUnit().primaryCoilCircuits);
      if (filtered.length !== 1) {
        this.workingUnit().primaryCoilCircuits = '0';
        if (this.workingUnit().request.PrimaryCoilRequest) {
          this.workingUnit().request.PrimaryCoilRequest.NumberOfCircuits = +this.workingUnit().primaryCoilCircuits;
        }
        setTimeout(() => {
          subject.next();
          subject.complete();
        }, 10);
      } else {
        this.modelService.setValue('USPrimaryCoilCircuit', this.engineKey(), this.workingUnit().primaryCoilCircuits.toString()).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                if (this.workingUnit().request.PrimaryCoilRequest) {
                  this.workingUnit().request.PrimaryCoilRequest.NumberOfCircuits = +this.workingUnit().primaryCoilCircuits;
                }
                subject.complete();
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      }
    } else {
      this.workingUnit().primaryCoilCircuits = '0';
      if (this.workingUnit().request.PrimaryCoilRequest) {
        this.workingUnit().request.PrimaryCoilRequest.NumberOfCircuits = +this.workingUnit().primaryCoilCircuits;
      }
      setTimeout(() => {
        subject.next();
        subject.complete();
      }, 10);
    }
    return subject.asObservable();
  }

  private handleSecondaryCoilCircuitsChange(): Observable<void> {
    const subject = new Subject<void>();
    if ((this.availableSecondaryCoilCircuits()?.length || 0) > 0) {
      const filtered = this.availableSecondaryCoilCircuits().filter(row => +row.code === +this.workingUnit().secondaryCoilCircuits);
      if (filtered.length !== 1) {
        this.workingUnit().secondaryCoilCircuits = '0';
        if (this.workingUnit().request.SecondaryCoilRequest) {
          this.workingUnit().request.SecondaryCoilRequest.NumberOfCircuits = +this.workingUnit().secondaryCoilCircuits;
        }
        setTimeout(() => {
          subject.next();
          subject.complete();
        }, 10);
      } else {
        this.modelService.setValue('USSecondaryCoilCircuit', this.engineKey(), this.workingUnit().secondaryCoilCircuits.toString()).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                if (this.workingUnit().request.SecondaryCoilRequest) {
                  this.workingUnit().request.SecondaryCoilRequest.NumberOfCircuits = +this.workingUnit().secondaryCoilCircuits;
                }
                subject.next();
                subject.complete();
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      }
    } else {
      this.workingUnit().secondaryCoilCircuits = '0';
      if (this.workingUnit().request.SecondaryCoilRequest) {
        this.workingUnit().request.SecondaryCoilRequest.NumberOfCircuits = +this.workingUnit().secondaryCoilCircuits;
      }
      setTimeout(() => {
        subject.next();
        subject.complete();
      }, 10);
    }
    return subject.asObservable();
  }

  private handleCoilRowsChange(): Observable<void> {
    const subject = new Subject<void>();
    if ((this.coilRows()?.length || 0) > 0) {
      const filtered = this.coilRows().filter(row => row.code === this.workingUnit().coilRowCode);
      if (filtered.length !== 1) {
        this.workingUnit().coilRowCode = undefined!;
        this.loadCoilCircuits().subscribe({
          complete: () => {
            this.handleCoilCircuitsChange().subscribe({
              complete: () => {
                this.loadElectricHeatKw().subscribe({
                  complete: () => {
                    this.handleElectricHeatKwChange().subscribe({
                      complete: () => {
                        subject.next();
                        subject.complete();
                      }, error: (error) => subject.error(error)
                    });
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      } else {
        this.modelService.setValue('USCoilRows', this.engineKey(), this.workingUnit().coilRowCode).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.modelService.getCharacteristicValue(this.engineKey(), 'USCoilRows', 'PriRows').subscribe({
                  next: (primaryRowCount) => {
                    this.modelService.getCharacteristicValue(this.engineKey(), 'USCoilRows', 'SecRows').subscribe({
                      next: (secondaryRowCount) => {
                        if (!isNaN(+primaryRowCount)) {
                          if (this.workingUnit().request.PrimaryCoilRequest) {
                            this.workingUnit().request.PrimaryCoilRequest.RowCount = +primaryRowCount;
                          }
                        }
                        if (!isNaN(+secondaryRowCount)) {
                          if (this.workingUnit().request.SecondaryCoilRequest) {
                            this.workingUnit().request.SecondaryCoilRequest.RowCount = +secondaryRowCount;
                          }
                        }
                        this.loadCoilCircuits().subscribe({
                          complete: () => {
                            this.handleCoilCircuitsChange().subscribe({
                              complete: () => {
                                this.loadElectricHeatKw().subscribe({
                                  complete: () => {
                                    this.handleElectricHeatKwChange().subscribe({
                                      complete: () => {
                                        subject.next();
                                        subject.complete();
                                      }, error: (error) => subject.error(error)
                                    });
                                  }, error: (error) => subject.error(error)
                                });
                              }, error: (error) => subject.error(error)
                            });
                          }, error: (error) => subject.error(error)
                        });
                      }, error: (error) => subject.error(error),
                    });
                  }, error: (error) => subject.error(error),
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      }
    } else {
      this.workingUnit().coilRowCode = undefined!;
      this.loadCoilCircuits().subscribe({
        complete: () => {
          this.handleCoilCircuitsChange().subscribe({
            complete: () => {
              this.loadElectricHeatKw().subscribe({
                complete: () => {
                  this.handleElectricHeatKwChange().subscribe({
                    complete: () => {
                      subject.next();
                      subject.complete();
                    }, error: (error) => subject.error(error)
                  });
                }, error: (error) => subject.error(error)
              });
            }, error: (error) => subject.error(error)
          });
        }, error: (error) => subject.error(error)
      });
    }
    return subject.asObservable();
  }

  private initialRatingRequest(): UnitRatingRequest {
    const request = new UnitRatingRequest();
    request.Model = this.workingUnit().modelCode;
    request.Size = this.workingUnit().sizeCode;
    if (this.isAirHandlerModel()) {
      request.FanSpeed = undefined!;
    } else {
      request.FanSpeed = 'H';
    }
    request.ExternalStaticPressure = 0;
    request.Altitude = 0;
    request.IsMetricInput = false;
    return request;
  }

  private buildDefaultElectricHeatRequest() {
    const request = new CoilRequest();
    const isMetric = this.workingUnit().request?.IsMetricInput || false;
    let dryBulb: number = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(70, true)) : 70;
    if (this.workingUnit() && this.workingUnit().request && this.workingUnit().request.ElectricHeatRequest) {
      if (this.workingUnit().request.IsMetricInput === isMetric) {
        dryBulb = this.workingUnit().request.ElectricHeatRequest.EnteringDryBulb;
      } else {
        dryBulb = this.round(UnitConversionUtils.convertTemperatureValue(this.workingUnit().request.ElectricHeatRequest.EnteringDryBulb, isMetric));
      }
    }
    request.EnteringDryBulb = dryBulb;
    request.isElectricHeat = true;
    return request;
  }

  private round(value: string): number {
    const numericValue = +value;
    const roundedValue = numericValue.toFixed(3);
    return +roundedValue;
  }

  private buildDefaultCoolingRequest(): CoilRequest {
    try {
      const isMetric = this.workingUnit().request?.IsMetricInput || false;
      const request = new CoilRequest();
      let dryBulb: number = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(75, true)) : 75;
      let wetBulb: number = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(63, true)) : 63;
      let enteringFluidTemp: number = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(45, true)) : 45;
      let satTemp = enteringFluidTemp;
      let flowRate: number = undefined!;
      let fluidType: string = 'W';
      let glycolPercent: number = 0;
      let temperatureChange: number = isMetric ? 5.6 : 10;
      let rowCount: number = (this.workingUnit().request?.PrimaryCoilRequest?.RowCount || 0);
      if (this.workingUnit()) {
        const coolingRequest: CoilRequest = this.getSelectedCoolingRequest(this.workingUnit());
        if (coolingRequest) {
          if (this.workingUnit().request.IsMetricInput === isMetric) {
            dryBulb = coolingRequest.EnteringDryBulb;
            wetBulb = coolingRequest.EnteringWetBulb;
            enteringFluidTemp = coolingRequest.EnteringFluidTemperature || enteringFluidTemp;
            satTemp = coolingRequest.SaturationSuctionTemperature || satTemp;
            temperatureChange = coolingRequest.TemperatureChange || undefined!;
            flowRate = coolingRequest.FlowRate || undefined!;
          } else {
            dryBulb = this.round(UnitConversionUtils.convertTemperatureValue(coolingRequest.EnteringDryBulb, isMetric));
            wetBulb = this.round(UnitConversionUtils.convertTemperatureValue(coolingRequest.EnteringWetBulb, isMetric));
            if (coolingRequest.EnteringFluidTemperature) {
              enteringFluidTemp = this.round(UnitConversionUtils.convertTemperatureValue(coolingRequest.EnteringFluidTemperature, isMetric));
            }
            if (coolingRequest.SaturationSuctionTemperature) {
              satTemp = this.round(UnitConversionUtils.convertTemperatureValue(coolingRequest.SaturationSuctionTemperature, isMetric));
            }
            temperatureChange = isMetric ? 5.6 : 10;
            flowRate = 0;
          }
          fluidType = coolingRequest.FluidType;
          glycolPercent = coolingRequest.GlycolPercentage;
          rowCount = coolingRequest.RowCount;
        }
      }
      request.EnteringDryBulb = dryBulb;
      request.EnteringWetBulb = wetBulb;
      if (this.isRefrigerant()) {
        request.SaturationSuctionTemperature = satTemp;
        request.FluidType = 'R410A';
        request.isRefrigerant = true;
      } else {
        request.EnteringFluidTemperature = enteringFluidTemp;
        request.FluidType = fluidType;
        request.GlycolPercentage = glycolPercent;
        request.isFluid = true;
      }
      request.isCooling = true;
      request.TemperatureChange = temperatureChange;
      request.FlowRate = flowRate;
      request.RowCount = rowCount;
      request.NumberOfCircuits = 0;
      return request;
    } catch (error) {
      this.handleError(error);
    }
    return undefined!;
  }

  private getSelectedCoolingRequest(unit: RatingUnit): CoilRequest {
    if (unit && unit.request && unit.request.PrimaryCoilRequest) {
      if (!unit.request.PrimaryCoilRequest.SaturationSuctionTemperature) {
        if (!unit.request.PrimaryCoilRequest.EnteringFluidTemperature) {
          return undefined!;
        } else {
          if (unit.request.PrimaryCoilRequest.EnteringFluidTemperature < unit.request.PrimaryCoilRequest.EnteringDryBulb) {
            return unit.request.PrimaryCoilRequest;
          } else {
            return undefined!;
          }
        }
      } else {
        return unit.request.PrimaryCoilRequest;
      }
    }
    return undefined!;
  }

  private buildDefaultHeatingRequest(): CoilRequest {
    try {
      const request = new CoilRequest();
      const isMetric = this.workingUnit().request?.IsMetricInput || false;
      let dryBulb = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(70, true)) : 70;
      let fluidTemperature = isMetric ? this.round(UnitConversionUtils.convertTemperatureValue(180, true)) : 180;
      let rowCount = (this.workingUnit().request?.PrimaryCoilRequest?.RowCount || 0);
      let fluidType = 'W';
      let steamPressure = isMetric ? this.round(UnitConversionUtils.convertSteamPressure(2, true)) : 2;
      let flowRate: number = undefined!;
      let glycolPercent: number = 0;
      let temperatureChange: number = isMetric ? 10 : 20;
      if (this.workingUnit()) {
        const heatRequest = this.getSelectedHeatingRequest(this.workingUnit());
        if (heatRequest) {
          if (this.workingUnit().request.IsMetricInput === isMetric) {
            dryBulb = heatRequest.EnteringDryBulb;
            fluidTemperature = heatRequest.EnteringFluidTemperature || fluidTemperature;
            steamPressure = heatRequest.EnteringSteamPressure || steamPressure;
            temperatureChange = heatRequest.TemperatureChange || undefined!;
            flowRate = heatRequest.FlowRate || undefined!;
          } else {
            dryBulb = this.round(UnitConversionUtils.convertTemperatureValue(heatRequest.EnteringDryBulb, isMetric));
            if (heatRequest.EnteringFluidTemperature) {
              fluidTemperature = this.round(UnitConversionUtils.convertTemperatureValue(heatRequest.EnteringFluidTemperature, isMetric));
            }
            if (heatRequest.EnteringSteamPressure) {
              steamPressure = this.round(UnitConversionUtils.convertSteamPressure(heatRequest.EnteringSteamPressure, isMetric));
            }
            temperatureChange = isMetric ? 10 : 20;
            flowRate = 0;
          }
          fluidType = heatRequest.FluidType;
          glycolPercent = heatRequest.GlycolPercentage;
          rowCount = heatRequest.RowCount;
        }
      }
      request.EnteringDryBulb = dryBulb;
      if (this.isSteam()) {
        request.EnteringSteamPressure = steamPressure;
        request.FluidType = 'W';
        request.isSteam = true;
      } else {
        request.EnteringFluidTemperature = fluidTemperature;
        request.FluidType = fluidType;
        request.TemperatureChange = temperatureChange;
        request.FlowRate = flowRate;
        request.GlycolPercentage = glycolPercent;
        request.isFluid = true;
      }
      request.RowCount = rowCount;
      request.NumberOfCircuits = 0;
      request.isCooling = false;
      request.isElectricHeat = false;
      return request;
    } catch (error) {
      this.handleError(error);
    }
    return undefined!;
  }

  private getSelectedHeatingRequest(unit: RatingUnit): CoilRequest {
    if (unit && unit.request) {
      if (unit.request.SecondaryCoilRequest) {
        if (!unit.request.SecondaryCoilRequest.EnteringSteamPressure) {
          if (!unit.request.SecondaryCoilRequest.EnteringFluidTemperature) {
            return undefined!;
          } else {
            if (unit.request.SecondaryCoilRequest.EnteringFluidTemperature > unit.request.SecondaryCoilRequest.EnteringDryBulb) {
              return unit.request.SecondaryCoilRequest;
            } else {
              return undefined!;
            }
          }
        } else {
          return unit.request.SecondaryCoilRequest;
        }
      } else if (unit.request.PrimaryCoilRequest) {
        if (!unit.request.PrimaryCoilRequest.EnteringSteamPressure) {
          if (!unit.request.PrimaryCoilRequest.EnteringFluidTemperature) {
            return undefined!;
          } else {
            if (unit.request.PrimaryCoilRequest.EnteringFluidTemperature > unit.request.PrimaryCoilRequest.EnteringDryBulb) {
              return unit.request.PrimaryCoilRequest;
            } else {
              return undefined!;
            }
          }
        } else {
          return unit.request.PrimaryCoilRequest;
        }
      } else {
        return undefined!;
      }
    }
    return undefined!;
  }

  selectedModelChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleModelChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error),
          next: () => this.isBusy.set(false)});
      }, error: (error) => this.handleError(error)
    });
  }

  private handleModelChange(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    const restoreLoadedFlag = this.initialLoadComplete();
    this.isAirHandlerModel.set(false);
    this.isSureflowModel.set(false);
    this.initialLoadComplete.set(false);
    this.availableSizes.set([]);
    this.availableMotorTypes.set([]);
    this.availableMotorVoltages.set([]);
    if ((this.workingUnit().modelCode?.trim()?.length || 0) > 0) {
      const filtered = this.allModels().filter(model => model.modelCode === this.workingUnit().modelCode);
      if (filtered.length === 1) {
        this.workingUnit().request.Model = this.workingUnit().modelCode;
        this.isAirHandlerModel.set(filtered[0].airHandlerModel);
        this.isSureflowModel.set(filtered[0].sureFlowModel);
        if (this.isAirHandlerModel() && this.workingUnit().request) {
          this.workingUnit().request.FanSpeed = undefined!;
        }
        this.modelService.setValue('USUnit', this.engineKey(), this.workingUnit().modelCode).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.unitSelectionService.getModelSizeCharacteristics(this.engineKey()).subscribe({
                  next: (characteristics) => {
                    this.availableSizes.set(characteristics);
                    this.handleSizeChange().subscribe({
                      complete: () => {
                        this.unitSelectionService.getEngineMotorVoltages(this.engineKey()).subscribe({
                          next: (voltages) => {
                            this.availableMotorVoltages.set(voltages);
                            this.handleSelectedMotorVoltageChange().subscribe({
                              complete: () => {
                                this.loadFilterTypes().subscribe({
                                  complete: () => {
                                    this.handleFilterTypeChange().subscribe({
                                      complete: () => {
                                        this.initialLoadComplete.set(restoreLoadedFlag);
                                        subject.complete();
                                      },
                                      error: (error) => subject.error(error)
                                    });
                                  }, error: (error) => subject.error(error)
                                });
                              }, error: (error) => subject.error(error)
                            });
                          }, error: (error) => subject.error(error)
                        });
                      }, error: (error) => subject.error(error)
                    });
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      } else {
        this.initialLoadComplete.set(restoreLoadedFlag);
        this.workingUnit().modelCode = undefined!;
        subject.complete();
      }
    } else {
      this.initialLoadComplete.set(restoreLoadedFlag);
      subject.complete();
    }
    return subject.asObservable();
  }

  filterTypeChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleFilterTypeChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          complete: () => this.isBusy.set(false),
          error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleFilterTypeChange(): Observable<void> {
    const subject = new Subject<void>();
    if (this.workingUnit().request?.Filter && (this.availableFilters()?.length || 0) > 0) {
      const filtered = this.availableFilters().filter(row => row.code === this.workingUnit().request.Filter);
      if (filtered.length !== 1) {
        this.workingUnit().request.Filter = this.availableFilters()[0].code;
      }
      subject.complete();
    } else {
      if (this.workingUnit().request) {
        this.workingUnit().request.Filter = undefined!;
      }
      subject.complete();
    }
    return subject.asObservable();
  }

  private loadFilterTypes(): Observable<void> {
    const subject = new Subject<void>();
    this.availableFilters.set([]);
    if (this.isAirHandlerModel()) {
      this.modelService.getEntity(this.engineKey(), 'USFilter').subscribe({
        next: (filterEntity) => {
          for (const allowable of filterEntity.Allowables) {
            if (allowable.IsValid) {
              const filter = new SelectionItem();
              filter.code = allowable.Value;
              filter.description = allowable.Description;
              this.availableFilters.update(current => [...current, filter]);
            }
          }
          subject.complete();
        }, error: (error) => subject.error(error)
      });
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  performanceInputChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.updateUnitRating().subscribe({
      next: () => this.isBusy.set(false),
      error: (error) => this.handleError(error),
      complete: () => this.isBusy.set(false)});
  }

  selectedMotorTypeChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleSelectedMotorTypeChange().subscribe({
      complete: () => {
        this.updateUnitRating().subscribe({
          next: () => this.isBusy.set(false),
          error: (error) => this.handleError(error),
          complete: () => this.isBusy.set(false)});
      }, error: (error) => this.handleError(error)
    });
  }

  private handleSelectedMotorTypeChange(): Observable<void> {
    const subject = new Subject<void>();
    if ((this.availableMotorTypes()?.length || 0) > 0) {
      const filtered: MotorType[] = this.availableMotorTypes().filter(motorType => motorType.guruModelValue === this.workingUnit().motorTypeCode);
      if (filtered.length !== 1) {
        this.workingUnit().motorTypeCode = undefined!;
        this.workingUnit().request.MotorType = undefined!;
        subject.complete();
      } else {
        this.modelService.setValue('USMotorType', this.engineKey(), this.workingUnit().motorTypeCode).subscribe({
          next: () => {
            this.modelService.isComplete(this.engineKey()).subscribe({
              next: () => {
                this.modelService.getCharacteristicValue(this.engineKey(), 'USMotorType', 'Code').subscribe({
                  next: (code) => {
                    this.workingUnit().request.MotorType = code;
                    this.loadElectricHeatKw().subscribe({
                      complete: () => {
                        this.handleElectricHeatKwChange().subscribe({
                          complete: () => subject.complete()
                          , error: (error) => subject.error(error)
                        });
                      }, error: (error) => subject.error(error)
                    });
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      }
    } else {
      this.workingUnit().motorTypeCode = undefined!;
      this.workingUnit().request.MotorType = undefined!;
      subject.complete();
    }
    return subject.asObservable();
  }

  selectedSizeChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.handleSizeChange().subscribe({
      complete: () => this.isBusy.set(false),
      error: (error) => this.handleError(error)
    });
  }

  private handleSizeChange(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    if (this.workingUnit().sizeCode && this.availableSizes()) {
      const filtered = this.availableSizes().filter(size => size.sizeCode === this.workingUnit().sizeCode);
      if (filtered.length === 1) {
        if (this.isAirHandlerModel() && this.initialLoadComplete()) {
          this.workingUnit().request.Airflow = filtered[0].nominalCfm;
        }
        this.modelService.setValue('USSize', this.engineKey(), this.workingUnit().sizeCode).subscribe({
          complete: () => {
            this.workingUnit().request.Size = this.workingUnit().sizeCode;
            this.handleCoilRowsChange().subscribe({
              complete: () => {
                this.updateUnitRating().subscribe({
                  next: () => subject.complete(),
                  complete: () => subject.complete(),
                  error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error)
            });
          }, error: (error) => subject.error(error)
        });
      } else {
        this.workingUnit().sizeCode = undefined!;
        subject.complete();
      }
    } else {
      this.workingUnit().sizeCode = undefined!;
      subject.complete();
    }
    return subject.asObservable();
  }

}
